import { isDraft } from 'immer';
import { keys } from '@direct-frontend/stdlib';

/**
 * Глубина сравнения каждого аргумента при кэширование селектора
 *
 * ВАЖНО!
 * Глубина должна быть одинаковой при проверке наличия draft перед сохранением в кэш
 * и при сравнении ключей кэширования
 *
 * Если это условие не будет соблюдено, то при использовании селектора в редьюсере
 * можно получить ошибку при обращении к Revoked Proxy
 */
const ARG_COMPARISON_DEPTH = 1;

/**
 * Функция является копией deepEqual из stdlib
 * Но добавляется дополнительное условие, которое скипает проверку draft иммера
 * @internal
 */
export function compareArg(arg1: unknown, arg2: unknown, maxDepth = ARG_COMPARISON_DEPTH) {
    // Сначала дешёвые проверки: примитивы (основной случай) не могут быть draft,
    // а isDraft заметно дороже Object.is и в горячем пути вызывается миллионы раз
    if (Object.is(arg1, arg2)) {
        return typeof arg1 !== 'object' || arg1 === null || !isDraft(arg1);
    }

    if (typeof arg1 !== 'object' || arg1 === null || typeof arg2 !== 'object' || arg2 === null) {
        return false;
    }

    // Если хоть один из аргументов является или вложено содержит draft, то считает аргументы разными
    if (isDraft(arg1) || isDraft(arg2)) {
        return false;
    }

    if (maxDepth === 0) {
        return false;
    }

    const keysA = keys(arg1);
    const keysB = keys(arg2);

    if (keysA.length !== keysB.length) {
        return false;
    }

    for (let i = 0; i < keysA.length; i++) {
        const keyA = keysA[i];

        if (!keyA) {
            return false;
        }

        if (!Object.prototype.hasOwnProperty.call(arg2, keyA) || !compareArg(arg1[keyA], arg2[keyA], maxDepth - 1)) {
            return false;
        }
    }

    return true;
}

/**
 * Функция проверяет что в переданное значение является draft или содержит draft вложено
 * @internal
 */
export function isDraftRecursive(obj: unknown, maxDepth = ARG_COMPARISON_DEPTH) {
    if (typeof obj !== 'object' || obj === null) {
        return false;
    }

    if (isDraft(obj)) {
        return true;
    }

    if (maxDepth === 0) {
        return false;
    }

    const argKeys = keys(obj);

    for (const k of argKeys) {
        if (isDraftRecursive(obj[k], maxDepth - 1)) {
            return true;
        }
    }

    return false;
}

/**
 * Фукнция проверяет можно ли создать прокси для переданного значения
 *
 * @internal
 */
export function canBeProxy(value: unknown): value is object {
    return typeof value === 'object' && value !== null;
}
